package com.evoapps.evoshift.data.repository

import com.evoapps.evoshift.data.dto.workplace.DayTemplateDto
import com.evoapps.evoshift.data.dto.workplace.RecurringCostRuleDto
import com.evoapps.evoshift.data.dto.workplace.ShiftTemplateDto
import com.evoapps.evoshift.data.dto.workplace.WorkplaceDto
import com.evoapps.evoshift.data.dto.workplace.WorkplaceFinancialsDto
import com.evoapps.evoshift.data.dto.workplace.WorkplaceSettingsDto
import com.evoapps.evoshift.data.mapper.AvailabilitySlotMapper
import com.evoapps.evoshift.data.mapper.DayTemplateMapper
import com.evoapps.evoshift.data.mapper.RecurringCostRuleMapper
import com.evoapps.evoshift.data.mapper.ShiftTemplateMapper
import com.evoapps.evoshift.data.mapper.toDto
import com.evoapps.evoshift.data.mapper.WorkplaceFinancialsMapper
import com.evoapps.evoshift.data.mapper.WorkplaceMapper
import com.evoapps.evoshift.data.mapper.WorkplaceSettingsMapper
import com.evoapps.evoshift.domain.model.AvailabilitySlot
import com.evoapps.evoshift.domain.model.DayTemplate
import com.evoapps.evoshift.domain.model.GeoLocation
import com.evoapps.evoshift.domain.model.RecurringCostRule
import com.evoapps.evoshift.domain.model.ShiftTemplate
import com.evoapps.evoshift.domain.model.SwapPolicy
import com.evoapps.evoshift.domain.model.Workplace
import com.evoapps.evoshift.domain.model.WorkplaceFinancials
import com.evoapps.evoshift.domain.model.WorkplaceSettings
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import com.evoapps.evoshift.data.remote.AppFirestore
import com.evoapps.evoshift.data.remote.CloudFunctionsClient
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put

class WorkplaceRepositoryImpl(
    private val firestore: AppFirestore,
    private val workplaceMapper: WorkplaceMapper,
    private val workplaceSettingsMapper: WorkplaceSettingsMapper,
    private val shiftTemplateMapper: ShiftTemplateMapper,
    private val recurringCostRuleMapper: RecurringCostRuleMapper,
    private val dayTemplateMapper: DayTemplateMapper,
    private val workplaceFinancialsMapper: WorkplaceFinancialsMapper,
    private val functions: CloudFunctionsClient
) : WorkplaceRepository {
    private val availabilitySlotMapper = AvailabilitySlotMapper()

    override suspend fun getWorkplace(id: String): Result<Workplace> {
        return try {
            val snapshot = firestore.collection("workplaces").document(id).get()
            if (snapshot.exists) {
                val dto = snapshot.data<WorkplaceDto>()
                Result.success(workplaceMapper.toDomain(dto))
            } else {
                Result.failure(Exception("Workplace not found"))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun createWorkplace(ownerId: String, name: String, availabilitySlots: List<AvailabilitySlot>): Result<String> {
        return try {
            val docRef = firestore.collection("workplaces").document
            val newId = docRef.id

            val initialSettings = WorkplaceSettingsDto(
                currency = "PLN",
                timezone = "Europe/Warsaw",
                firstDayOfWeek = 1,
                availableRoles = emptyList(),
                workplaceSkills = emptyList(),
                shiftSwapPolicy = SwapPolicy.B_APPROVAL.name,
                defaultWeeklySkeleton = emptyMap(),
                definedAvailabilitySlots = availabilitySlots.map(availabilitySlotMapper::toDto),
                // SH-223: a new workplace starts on the recommended request types (older ones keep theirs).
                requests = workplaceSettingsMapper.requestsDto(com.evoapps.evoshift.domain.model.RequestSettings())
            )

            val newWorkplaceDto = WorkplaceDto(
                id = newId,
                ownerId = ownerId,
                name = name,
                settings = initialSettings,
                address = ""
            )

            // No sales plan until the owner gives one (onboarding asks, SH-61): sample targets
            // would show made-up labour percentages.
            val initialFinancials = WorkplaceFinancialsDto(salesTargets = emptyMap())

            // Zapisujemy w transakcji/batchu, żeby oba dokumenty powstały naraz
            val batch = firestore.batch()
            batch.set(docRef, newWorkplaceDto)
            batch.set(docRef.collection("financials").document("data"), initialFinancials)
            batch.commit()

            Result.success(newId)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun updateSettings(
        workplaceId: String,
        settings: WorkplaceSettings
    ): Result<Unit> {
        return try {
            val dto = workplaceSettingsMapper.toDto(settings)
            
            // Firestore map keys must be strings; inside updateFields `to` is the field DSL, so build it first.
            val skeleton = dto.defaultWeeklySkeleton.entries.associate { (day, template) -> day.toString() to template }
            firestore.collection("workplaces").document(workplaceId).updateFields {
                "settings.timezone" to dto.timezone
                "settings.currency" to dto.currency
                "settings.firstDayOfWeek" to dto.firstDayOfWeek
                "settings.availableRoles" to dto.availableRoles
                "settings.workplaceSkills" to dto.workplaceSkills
                "settings.leadRoles" to dto.leadRoles
                "settings.leadSkills" to dto.leadSkills
                "settings.shiftSwapPolicy" to dto.shiftSwapPolicy
                "settings.defaultWeeklySkeleton" to skeleton
                "settings.definedAvailabilitySlots" to dto.definedAvailabilitySlots
                "settings.market" to dto.market
                "settings.payroll" to dto.payroll
                "settings.noShowAlertMinutes" to dto.noShowAlertMinutes
                "settings.checklistAlertMinutes" to dto.checklistAlertMinutes
                "settings.blockExpiredStaffChecks" to dto.blockExpiredStaffChecks
                "settings.restRulesForAllContracts" to dto.restRulesForAllContracts
                "settings.sundayPolicy" to dto.sundayPolicy
                "settings.geofence" to dto.geofence
                "settings.breakPolicy" to dto.breakPolicy
                "settings.clockReview" to dto.clockReview
                "settings.workHours" to dto.workHours
                "settings.weatherEnabled" to dto.weatherEnabled
                "settings.specialDaysEnabled" to dto.specialDaysEnabled
                "settings.voivodeship" to dto.voivodeship
                "settings.showEvoShiftFooter" to dto.showEvoShiftFooter
                "settings.reports" to dto.reports
                // SH-255 (rosterHasHiddenPeople is the setRosterHidden function's, never written here).
                "settings.rosterVisibility" to dto.rosterVisibility
            }
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun updateRequestSettings(workplaceId: String, requests: com.evoapps.evoshift.domain.model.RequestSettings): Result<Unit> = runCatching {
        val dto = workplaceSettingsMapper.requestsDto(requests)
        firestore.collection("workplaces").document(workplaceId).updateFields { "settings.requests" to dto }
    }

    override suspend fun updateUnpaidWork(
        workplaceId: String,
        periods: Map<String, List<com.evoapps.evoshift.domain.budget.UnpaidPeriod>>,
        unpaidUserIds: List<String>,
        asOf: String
    ): Result<Unit> = runCatching {
        // Field by field: the rest of the payroll settings stay (an older app's save keeps the list it read).
        val stored = workplaceSettingsMapper.unpaidPeriodsDto(periods)
        firestore.collection("workplaces").document(workplaceId).updateFields {
            "settings.unpaidPeriods" to stored
            "settings.unpaidPeriodsAsOf" to asOf
            "settings.payroll.unpaidUserIds" to unpaidUserIds
        }
    }

    override suspend fun updateWorkplaceName(workplaceId: String, newName: String): Result<Unit> {
        return try {
            firestore.collection("workplaces").document(workplaceId).updateFields { "name" to newName }
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun updateWorkplaceLocation(
        workplaceId: String,
        address: String,
        location: GeoLocation?
    ): Result<Unit> {
        return try {
            // The numbers for SH-106 and the legacy text for apps before SH-110, the same point.
            firestore.collection("workplaces").document(workplaceId).updateFields {
                "address" to address
                "location" to location?.toDto()
                "geoplace" to location?.format()
            }
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun updateWorkplaceFinancials(
        workplaceId: String,
        financials: WorkplaceFinancials
    ): Result<Unit> {
        return try {
            val dto = workplaceFinancialsMapper.toDto(financials)
            firestore.collection("workplaces").document(workplaceId)
                .collection("financials").document("data")
                .set(dto)
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun getWorkplaceFinancials(workplaceId: String): Result<WorkplaceFinancials> {
        return try {
            val snapshot = firestore.collection("workplaces").document(workplaceId)
                .collection("financials").document("data").get()
            if (snapshot.exists) {
                val dto = snapshot.data<WorkplaceFinancialsDto>()
                Result.success(workplaceFinancialsMapper.toDomain(dto))
            } else {
                // Zwracamy domyślny obiekt, jeśli nie znaleziono dokumentu
                Result.success(WorkplaceFinancials())
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    /**
     * The owner deletes the workplace with everything that belongs to it, server-side
     * (functions/workplaceDeletion.js): other people's pay terms, engagements and records the
     * client may not touch go too (SH-50).
     */
    override suspend fun deleteWorkplace(id: String): Result<Unit> = runCatching {
        functions.call(
            "deleteWorkplace",
            buildJsonObject {
                put("workplaceId", id)
                put("confirm", "DELETE")
            }
        )
    }

    override suspend fun updateDayTemplate(
        workplaceId: String,
        template: DayTemplate
    ): Result<Unit> {
        return try {
            val dto = dayTemplateMapper.toDto(template)
            firestore.collection("workplaces").document(workplaceId)
                .collection("day_templates").document(template.id)
                .set(dto)

            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun deleteDayTemplate(workplaceId: String, templateId: String): Result<Unit> {
        return try {
            firestore.collection("workplaces").document(workplaceId)
                .collection("day_templates").document(templateId)
                .delete()
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun updateWeeklySkeleton(
        workplaceId: String,
        skeleton: Map<Int, String?>
    ): Result<Unit> {
        return try {
            val firestoreMap = skeleton.entries.associate { (day, templateId) ->
                day.toString() to templateId
            }

            firestore.collection("workplaces").document(workplaceId)
                .updateFields { "settings.defaultWeeklySkeleton" to firestoreMap }
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun createShiftTemplate(
        workplaceId: String,
        template: ShiftTemplate
    ): Result<String> {
        return try {
            val docRef = firestore.collection("workplaces").document(workplaceId)
                .collection("shift_templates").document

            val newId = docRef.id
            val dto = shiftTemplateMapper.toDto(template).copy(id = newId)

            docRef.set(dto)
            Result.success(newId)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun updateShiftTemplate(
        workplaceId: String,
        template: ShiftTemplate
    ): Result<Unit> {
        return try {
            val dto = shiftTemplateMapper.toDto(template)
            firestore.collection("workplaces").document(workplaceId)
                .collection("shift_templates").document(template.id)
                .set(dto)
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun deleteShiftTemplate(
        workplaceId: String,
        templateId: String,
        dayTemplatesWithoutIt: List<DayTemplate>
    ): Result<Unit> {
        return try {
            val workplace = firestore.collection("workplaces").document(workplaceId)
            // SH-190: one batch, so no day template is left pointing at a deleted shift template.
            val batch = firestore.batch()
            batch.delete(workplace.collection("shift_templates").document(templateId))
            dayTemplatesWithoutIt.forEach { day ->
                batch.updateFields(workplace.collection("day_templates").document(day.id)) {
                    "shiftTemplateIds" to day.shiftTemplateIds
                }
            }
            batch.commit()
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun getShiftTemplates(workplaceId: String): Result<List<ShiftTemplate>> {
        return try {
            val snapshot = firestore.collection("workplaces").document(workplaceId)
                .collection("shift_templates").get()

            val templates = snapshot.documents.map {
                shiftTemplateMapper.toDomain(it.data<ShiftTemplateDto>())
            }
            Result.success(templates)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun getDayTemplates(workplaceId: String): Result<List<DayTemplate>> {
        return try {
            val snapshot = firestore.collection("workplaces").document(workplaceId)
                .collection("day_templates").get()

            val templates = snapshot.documents.map {
                dayTemplateMapper.toDomain(it.data<DayTemplateDto>())
            }
            Result.success(templates)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }
    override suspend fun getRecurringCostRules(ownerId: String): Result<List<RecurringCostRule>> {
        return try {
            val snapshot = firestore.collection("owners").document(ownerId)
                .collection("recurring_cost_rules").get()

            val rules = snapshot.documents.map {
                recurringCostRuleMapper.toDomain(it.data<RecurringCostRuleDto>())
            }
            Result.success(rules)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun getRecurringCostRulesFor(ownerId: String, workplaceId: String): Result<List<RecurringCostRule>> = runCatching {
        // Constrained by workplace, so the rules can admit the workplace's financial managers.
        firestore.collection("owners").document(ownerId).collection("recurring_cost_rules")
            .where { "targetWorkplaceId" equalTo workplaceId }
            .get().documents
            .map { recurringCostRuleMapper.toDomain(it.data<RecurringCostRuleDto>()) }
    }

    override suspend fun createRecurringCostRule(
        ownerId: String,
        rule: RecurringCostRule
    ): Result<Unit> {
        return try {
            val docRef = firestore.collection("owners").document(ownerId)
                .collection("recurring_cost_rules").document

            val dto = recurringCostRuleMapper.toDto(rule).copy(uid = docRef.id)

            docRef.set(dto)
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun updateRecurringCostRule(
        ownerId: String,
        rule: RecurringCostRule
    ): Result<Unit> {
        return try {
            val docRef = firestore.collection("owners").document(ownerId)
                .collection("recurring_cost_rules").document(rule.id)

            val dto = recurringCostRuleMapper.toDto(rule)

            docRef.set(dto)
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun deleteRecurringCostRule(
        ownerId: String,
        ruleId: String
    ): Result<Unit> {
        return try {
            firestore.collection("owners").document(ownerId)
                .collection("recurring_cost_rules").document(ruleId).delete()
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun createDayTemplate(
        workplaceId: String,
        template: DayTemplate
    ): Result<String> {
        return try {
            val collectionRef = firestore.collection("workplaces")
                .document(workplaceId)
                .collection("day_templates")

            val newId = generateFirestoreId()
            val docRef = collectionRef.document(newId)

            val templateWithId = template.copy(id = newId)

            val dto = dayTemplateMapper.toDto(templateWithId)

            docRef.set(dto)

            Result.success(newId)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    fun generateFirestoreId(): String {
        val chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789"
        return (1..20)
            .map { chars.random() }
            .joinToString("")
    }
}